/**
 * Chat Manager
 * Handles chat message operations and UI
 *
 * @module ChatManager
 * Sprint 32: OOA/OOD Refactoring
 */

import { ChatMessage, DocumentSource } from '../types/ii-agent-types';
import { formatMarkdown, unescapeString } from '../utils/markdown-formatter';
import { escapeHtml, scrollToBottom, generateMessageId } from '../utils/html-utils';

/**
 * Interface for chat manager
 */
export interface IChatManager {
    getMessages(): ChatMessage[];
    addMessage(sender: 'user' | 'agent', content: string, isTyping?: boolean, data?: any): string;
    clearMessages(): void;
    exportToFile(): void;
    displayDocumentSources(sources: DocumentSource[], afterMessageId: string): void;
}

/**
 * Manager for chat message operations
 */
export class ChatManager implements IChatManager {
    private messages: ChatMessage[] = [];
    private containerId: string;

    constructor(containerId: string = 'chat-messages') {
        this.containerId = containerId;
    }

    /**
     * Get all chat messages
     */
    getMessages(): ChatMessage[] {
        return [...this.messages];
    }

    /**
     * Get messages excluding typing indicators
     */
    getMessagesWithoutTyping(): ChatMessage[] {
        return this.messages.filter(m => !m.data?.isTyping);
    }

    /**
     * Add a message to the chat
     */
    addMessage(sender: 'user' | 'agent', content: string, isTyping: boolean = false, data: any = null): string {
        const container = document.getElementById(this.containerId);
        const messageId = generateMessageId();

        if (!container) {
            console.warn('Chat container not found:', this.containerId);
            return messageId;
        }

        const messageDiv = document.createElement('div');
        messageDiv.id = messageId;
        messageDiv.className = `mb-4 ${sender === 'user' ? 'text-right' : 'text-left'}`;

        const bubbleClass = sender === 'user'
            ? 'bg-blue-500 text-white'
            : 'bg-gray-100 text-gray-800';

        const formattedContent = formatMarkdown(content);

        messageDiv.innerHTML = `
            <div class="inline-block max-w-3/4 ${bubbleClass} rounded-lg px-4 py-2 text-left chat-bubble">
                ${isTyping ? '<i class="animate-pulse">Thinking...</i>' : formattedContent}
            </div>
            ${data && !isTyping ? `<div class="text-xs text-gray-500 mt-1">Analysis complete</div>` : ''}
        `;

        container.appendChild(messageDiv);
        scrollToBottom(container);

        // Store message
        const messageData = isTyping ? { ...data, isTyping: true } : data;
        this.messages.push({ sender, content, timestamp: Date.now(), data: messageData });

        return messageId;
    }

    /**
     * Remove a message by ID (e.g., typing indicator)
     */
    removeMessage(messageId: string): void {
        const element = document.getElementById(messageId);
        if (element) {
            element.remove();
        }
    }

    /**
     * Update the last agent message with additional content
     */
    appendToLastMessage(additionalHtml: string): void {
        const container = document.getElementById(this.containerId);
        if (!container) return;

        const lastMessage = container.lastElementChild;
        if (lastMessage) {
            const contentDiv = lastMessage.querySelector('.chat-bubble');
            if (contentDiv) {
                contentDiv.innerHTML += additionalHtml;
            }
        }
    }

    /**
     * Clear all chat messages
     */
    clearMessages(): void {
        this.messages = [];

        const container = document.getElementById(this.containerId);
        if (!container) {
            console.warn('Chat container not found');
            return;
        }

        // Reset to empty state
        container.innerHTML = `
            <div class="text-center text-gray-500 text-sm mt-8">
                <div class="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 via-blue-500 to-cyan-400 flex items-center justify-center mx-auto mb-3 opacity-50">
                    <svg class="w-7 h-7 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <circle cx="12" cy="12" r="3" fill="currentColor"/>
                        <path d="M12 2v4M12 18v4M2 12h4M18 12h4" stroke-linecap="round"/>
                        <circle cx="12" cy="12" r="8" stroke-dasharray="2 3"/>
                    </svg>
                </div>
                <p class="font-medium">Start a conversation with Ada</p>
                <p class="text-xs mt-2 max-w-md mx-auto">Ask about your infrastructure, security vulnerabilities, cost optimization, or get help documenting business services.</p>
            </div>
        `;

        console.log('Chat cleared');
    }

    /**
     * Export chat to markdown file
     */
    exportToFile(): void {
        const messages = this.getMessagesWithoutTyping();

        if (messages.length === 0) {
            if (typeof window !== 'undefined' && (window as any).showToastNotification) {
                (window as any).showToastNotification('No messages to save', 'warning');
            } else {
                alert('No messages to save');
            }
            return;
        }

        // Format as markdown
        const now = new Date();
        const dateStr = now.toISOString().split('T')[0];
        const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-');

        let content = `# Ada Chat Conversation\n\n`;
        content += `**Date:** ${now.toLocaleString()}\n\n`;
        content += `---\n\n`;

        messages.forEach((msg) => {
            const sender = msg.sender === 'user' ? 'You' : 'Ada';
            const time = new Date(msg.timestamp).toLocaleTimeString();
            content += `### ${sender} (${time})\n\n`;
            content += `${msg.content}\n\n`;
        });

        content += `---\n\n`;
        content += `*Exported from ASD - Ask Ada*\n`;

        // Create and trigger download
        const blob = new Blob([content], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ada-chat-${dateStr}-${timeStr}.md`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        if (typeof window !== 'undefined' && (window as any).showToastNotification) {
            (window as any).showToastNotification('Chat saved successfully', 'success');
        }
    }

    /**
     * Display document sources from RAG retrieval
     */
    displayDocumentSources(sources: DocumentSource[], _afterMessageId: string): void {
        if (sources.length === 0) return;

        const container = document.getElementById(this.containerId);
        if (!container) return;

        const sourcesDiv = document.createElement('div');
        sourcesDiv.className = 'ml-4 mt-2 text-left';

        const sourcesHtml = sources.map((source) => {
            const confidencePercent = Math.round(source.similarity_score * 100);
            const confidenceColor = confidencePercent >= 70 ? 'text-green-600' :
                                    confidencePercent >= 50 ? 'text-amber-600' : 'text-gray-500';

            return `
                <div class="text-xs bg-blue-50 border border-blue-200 rounded px-2 py-1 mb-1">
                    <div class="flex items-center justify-between">
                        <span class="font-medium text-blue-700">${escapeHtml(source.document_name)}</span>
                        <span class="${confidenceColor}">${confidencePercent}% match</span>
                    </div>
                    ${source.page_number ? `<span class="text-gray-500 text-xs">Page ${source.page_number}</span>` : ''}
                    <p class="text-gray-600 mt-1 line-clamp-2">${escapeHtml(source.chunk_preview)}</p>
                </div>
            `;
        }).join('');

        sourcesDiv.innerHTML = `
            <details class="text-xs">
                <summary class="cursor-pointer text-blue-600 hover:text-blue-800 select-none">
                    ${sources.length} source${sources.length === 1 ? '' : 's'} referenced
                </summary>
                <div class="mt-2 space-y-1">
                    ${sourcesHtml}
                </div>
            </details>
        `;

        container.appendChild(sourcesDiv);
        scrollToBottom(container);
    }

    /**
     * Load messages from an array (e.g., from history)
     */
    loadMessages(messages: Array<{role: string, content: string}>): void {
        this.clearMessages();

        // Add a small delay to ensure UI is ready
        setTimeout(() => {
            messages.forEach(msg => {
                const sender = msg.role === 'user' ? 'user' : 'agent';
                this.addMessage(sender, msg.content);
            });
        }, 100);
    }

    /**
     * Unescape string utility (exposed for external use)
     */
    unescapeContent(text: string): string {
        return unescapeString(text);
    }
}
